import { useState, useEffect } from "react";
import { isPropertyKind } from "@/lib/clientTypes";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { T, F, Btn, MainCtaScope, th as palette } from "@/lib/bricks";
import { PageHeader } from "@/components/calm/Calm";
import { workspaceKind } from "@/lib/workspace";
import { useShellDark } from "@/lib/useShellDark";
import PasskeysCard from "@/components/PasskeysCard";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { requestAccountDeletion, DELETION_INFO_URL, SUPPORT_EMAIL } from "@/lib/webAccount";
import { NOTIF_DEFAULTS, rowsFor, fromServer, flip } from "@/lib/notifPrefs";
import { pushSupport, supportMessage, currentSubscription, enablePush, disablePush } from "@/lib/webPush";

function DisplayRow({ label, value, last }) {
  return (
    <div
      style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'16px var(--bx-pad)',
        borderBottom: last ? 'none' : undefined,
        gap:20, minWidth:0,
      }}
    >
      <span style={{ fontSize:14, color:'inherit' }}>{label}</span>
      <span style={{ fontSize:14, color:'inherit', opacity:0.65, minWidth:0, overflowWrap:'anywhere', textAlign:'right' }}>{value || 'Not set'}</span>
    </div>
  );
}

function Card({ p, title, children }) {
  return (
    <section style={{ color:p.ink, '--bx-pad':'4px' }}>
      <h2 style={{ fontSize:16, fontWeight:500, letterSpacing:-0.2, margin:'0 0 10px', color:p.ink, fontFamily:F.display }}>{title}</h2>
      <div style={{ borderTop:`1px solid ${p.hair}`, borderBottom:`1px solid ${p.hair}` }}>{children}</div>
    </section>
  );
}

function AppearanceCard({ p, theme, setTheme, themeOptions }) {
  return (
    <Card p={p} title="Appearance">
      <div style={{ padding:'16px var(--bx-pad)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ fontSize:14 }}>Theme</span>
        <div style={{ display:'flex', gap:2 }}>
          {themeOptions.map(opt => {
            const active = theme === opt.value;
            return (
              <button
                key={opt.value}
                data-testid={`theme-${opt.value}`}
                onClick={() => setTheme(opt.value)}
                style={{ padding:'8px 16px', minHeight:44, borderRadius:2, fontSize:13, fontFamily:F.display, cursor:'pointer', background: active ? p.surf2 : 'transparent', color: active ? p.ink : p.ink3, border:`1px solid ${active ? p.hairS : 'transparent'}`, fontWeight: active ? 500 : 400 }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function PushDeviceRow({ p, chip, pushOn }) {
  const [support] = useState(() => pushSupport());
  const [sub, setSub] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState(support.ok ? '' : supportMessage(support.reason));

  useEffect(() => {
    let live = true;
    if (!support.ok) { setChecked(true); return undefined; }
    currentSubscription().then(s => { if (live) { setSub(s); setChecked(true); } }).catch(() => { if (live) setChecked(true); });
    return () => { live = false; };
  }, [support.ok]);

  const turnOn = async () => {
    setBusy('on'); setNote('');
    try {
      const s = await enablePush(api);
      setSub(s);
      toast.success('Push is on for this device');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Could not turn on push';
      setNote(msg); toast.error(msg);
    } finally { setBusy(''); }
  };
  const turnOff = async () => {
    setBusy('off');
    try { await disablePush(api); setSub(null); toast.success('Push is off for this device'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not turn off push'); }
    finally { setBusy(''); }
  };
  const test = async () => {
    setBusy('test');
    try {
      const { data } = await api.post('/push/test');
      if (data && data.sent > 0) toast.success(`Test sent to ${data.sent} device${data.sent === 1 ? '' : 's'}`);
      else toast.error('Test didn’t arrive. Turn push off and on again.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send a test'); }
    finally { setBusy(''); }
  };

  const status = !support.ok ? 'Not available in this browser'
    : !checked ? 'Checking…'
    : sub ? (pushOn ? 'On for this device' : 'New booking push is off above')
    : 'Off for this device';

  return (
    <div data-testid="notif-push-device" style={{ padding:'16px var(--bx-pad)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap', borderBottom:`1px solid ${p.hair}` }}>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:14 }}>Push on this device</div>
        <div style={{ fontSize:12, color:p.ink3, marginTop:4, lineHeight:1.45 }}>{note || status}</div>
      </div>
      {support.ok && checked && (
        <div style={{ display:'flex', gap:6 }}>
          {sub ? (<>
            <button type="button" data-testid="push-test" disabled={!!busy} onClick={test} style={chip(false)}>{busy === 'test' ? 'Sending…' : 'Send test'}</button>
            <button type="button" data-testid="push-off" disabled={!!busy} onClick={turnOff} style={chip(true)}>{busy === 'off' ? 'Turning off…' : 'Turn off'}</button>
          </>) : (
            <button type="button" data-testid="push-on" disabled={!!busy} onClick={turnOn} style={chip(true)}>{busy === 'on' ? 'Turning on…' : 'Turn on'}</button>
          )}
        </div>
      )}
    </div>
  );
}

function NotifCard({ p, email, kind }) {
  const [prefs, setPrefs] = useState(NOTIF_DEFAULTS);
  const [stored, setStored] = useState(null);
  const [busy, setBusy] = useState('');
  const rows = rowsFor(kind);

  useEffect(() => {
    let live = true;
    api.get('/settings/notifications').then(r => {
      if (!live) return;
      const { prefs: pr, stored: st } = fromServer(r.data);
      setPrefs(pr); setStored(st);
    }).catch(() => { if (live) setStored(false); });
    return () => { live = false; };
  }, []);

  const toggle = async (field) => {
    if (busy) return;
    const before = prefs;
    const [next, body] = flip(prefs, field);
    setPrefs(next); setBusy(field);
    try {
      const { data } = await api.put('/settings/notifications', body);
      const { prefs: pr, stored: st } = fromServer(data);
      setPrefs(pr); setStored(st);
    } catch (e) {
      setPrefs(before);
      toast.error(e?.response?.data?.detail || 'Could not save notification settings');
    } finally {
      setBusy('');
    }
  };

  const preview = async () => {
    const w = window.open('', '_blank');
    try {
      const { data } = await api.get('/settings/notifications/digest-preview');
      if (w) { w.document.title = data.subject || 'Weekly digest'; w.document.body.innerHTML = data.html || ''; }
    } catch (e) {
      if (w) w.close();
      toast.error(e?.response?.data?.detail || 'Could not build the digest preview');
    }
  };

  const chip = (on) => ({
    padding:'7px 14px', borderRadius:2, fontSize:13, fontFamily:F.display, cursor:'pointer', minHeight:44,
    background: on ? p.surf2 : 'transparent', color: on ? p.ink : p.ink3,
    border:`1px solid ${on ? p.hairS : p.hair}`, fontWeight: on ? 500 : 400,
  });

  return (
    <Card p={p} title="Notifications">
      {rows.map((r, i) => (
        <div key={r.key} data-testid={`notif-${r.key}`}
          style={{ padding:'16px var(--bx-pad)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap', borderBottom:`1px solid ${p.hair}` }}>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:14 }}>{r.label}</div>
            <div style={{ fontSize:12, color:p.ink3, marginTop:4 }}>
              {r.sub}
              {r.digest && prefs.weekly_digest && (
                <> · <button type="button" onClick={preview} data-testid="notif-digest-preview"
                  style={{ background:'none', border:'none', padding:0, color:p.ink2, textDecoration:'underline', textUnderlineOffset:3, cursor:'pointer', fontSize:12, fontFamily:F.display }}>Preview</button></>
              )}
            </div>
          </div>
          <div style={{ display:'flex', gap:6 }}>
            {r.channels.map(c => {
              const on = !!prefs[c.field];
              return (
                <button key={c.field} type="button" role="switch" aria-checked={on}
                  aria-label={`${r.label} ${c.label}`} data-testid={`notif-${c.field}`}
                  disabled={stored === null || !!busy} onClick={() => toggle(c.field)} style={chip(on)}>
                  {c.label} {on ? 'on' : 'off'}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {rows.some(r => r.channels.some(c => c.field === 'new_booking_push')) && <PushDeviceRow p={p} chip={chip} pushOn={!!prefs.new_booking_push}/>}
      <div style={{ padding:'16px var(--bx-pad)', fontSize:12, color:p.ink3, lineHeight:1.45 }}>
        {stored === false
          ? 'Couldn’t load settings. Changes won’t save right now.'
          : <>Emails go to {email || 'your account email'}. Receipts and security mail always send.</>}
      </div>
    </Card>
  );
}

// Same request the native OS app files (Settings → Delete account).
function DeleteAccountCard({ p }) {
  const [step, setStep] = useState('idle');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState(null);
  const danger = 'var(--c-attn)';
  const btn = { padding:'10px 16px', borderRadius:2, fontFamily:F.display, fontSize:13, cursor:'pointer', minHeight:44 };

  const send = async () => {
    setStep('sending');
    const { data } = await supabase.auth.getUser();
    const u = data && data.user;
    const r = await requestAccountDeletion(supabase, u ? { id: u.id, email: u.email } : null, reason);
    setResult(r);
    setStep('idle');
  };

  return (
    <Card p={p} title="Delete account">
      <div data-testid="delete-account" style={{ padding:'16px var(--bx-pad)', display:'flex', flexDirection:'column', gap:14 }}>
        <div style={{ fontSize:13, color:p.ink3, lineHeight:1.5 }}>
          Removes your Bricks login, profile and connected tools within 30 days. Records Bricks must keep by law
          (invoices, completed orders) are kept. <a href={DELETION_INFO_URL} target="_blank" rel="noreferrer" style={{ color:p.ink2, textUnderlineOffset:3 }}>Details</a>
        </div>
        {result && result.ok && (
          <div data-testid="delete-account-done" style={{ fontSize:14 }}>
            {result.already ? 'Your deletion request is already in. ' : 'Request received. '}We will email you when it is done. Questions: {SUPPORT_EMAIL}
          </div>
        )}
        {result && !result.ok && (
          <div style={{ fontSize:13, color:danger }}>{result.error} <a href={result.mailto} style={{ color:danger }}>Email {SUPPORT_EMAIL}</a></div>
        )}
        {!(result && result.ok) && step === 'idle' && (
          <div>
            <button type="button" data-testid="delete-account-start" onClick={() => setStep('confirm')}
              style={{ ...btn, background:'transparent', color:danger, border:`1px solid ${danger}` }}>Delete my account</button>
          </div>
        )}
        {step !== 'idle' && (
          <>
            <label style={{ fontSize:12, color:p.ink3 }}>Reason (optional)
              <input value={reason} onChange={e => setReason(e.target.value)}
                style={{ display:'block', marginTop:6, width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:'transparent', outline:'none', boxSizing:'border-box' }}/>
            </label>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <button type="button" data-testid="delete-account-confirm" onClick={send} disabled={step === 'sending'}
                style={{ ...btn, background:danger, color:'var(--c-solid-text)', border:`1px solid ${danger}`, opacity: step === 'sending' ? 0.6 : 1 }}>
                {step === 'sending' ? 'Sending…' : 'Yes, delete my account'}
              </button>
              <button type="button" onClick={() => setStep('idle')} disabled={step === 'sending'}
                style={{ ...btn, background:'transparent', color:p.ink, border:`1px solid ${p.hair}` }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const kind = workspaceKind(user);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [officeDraft, setOfficeDraft] = useState({ brokerage_name:'', notification_email:'', monthly_budget:'' });
  const [teamDraft, setTeamDraft] = useState('');
  const { theme, setTheme } = useTheme();
  const dark = useShellDark();
  const p = palette(dark);

  useEffect(() => {
    api.get("/settings").then(r => {
      const data = r.data || {};
      setSettings(data);
      setOfficeDraft({
        brokerage_name: data.brokerage_name || data.office_name || '',
        notification_email: data.notification_email || '',
        monthly_budget: data.monthly_budget != null ? String(data.monthly_budget) : '',
      });
      setTeamDraft(data.team_name || '');
    }).catch(() => {
      setSettings({});
    });
  }, []);

  const saveOffice = async () => {
    setSaving(true);
    try {
      const body = {
        brokerage_name: officeDraft.brokerage_name || null,
        notification_email: officeDraft.notification_email || null,
        monthly_budget: officeDraft.monthly_budget === '' ? null : Number(officeDraft.monthly_budget),
      };
      const { data } = await api.put("/settings", body);
      setSettings(data || {});
      toast.success("Office settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save office settings");
    } finally {
      setSaving(false);
    }
  };

  const saveTeam = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { team_name: teamDraft || null });
      setSettings(data || {});
      toast.success("Team name saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save team name");
    } finally {
      setSaving(false);
    }
  };

  const themeOptions = [
    { value:'light', label:'Light' },
    { value:'dark',  label:'Dark' },
    { value:'system', label:'System' },
  ];

  const notifEmail = settings?.notification_email || user?.email || '';
  const profileName = settings?.name || user?.name || '';
  const profileEmail = settings?.email || user?.email || '';

  const subtitle = {
    platform: 'Platform preferences',
    brokerage_manager: 'Office preferences',
    team_lead: 'Team preferences',
    team_member: 'Your account',
    agent: 'Your account',
    solo: 'Your account',
    builder: 'Your account',
    reit_leasing: 'Your account',
  }[kind] || (isPropertyKind(kind) ? 'Your account' : 'Preferences');
  const isBuilder = isPropertyKind(kind);

  return (
    <div data-testid="settings-page" style={{ display:'flex', flexDirection:'column', gap:36, fontFamily:F.display, color:p.ink, maxWidth:700, paddingBottom:96 }}>

      <PageHeader title="Settings" subtitle={subtitle} />

      {kind === 'platform' && (
        <>
          <Card p={p} title="Platform">
            <DisplayRow label="Legal name" value="Bricks Technologies Inc"/>
            <div style={{ borderBottom:`1px solid ${p.hair}` }}/>
            <DisplayRow label="Ops email" value="booking@brxs.ca" last/>
          </Card>
          <Card p={p} title="Network">
            <DisplayRow label="Hiring queue" value="/hiring"/>
            <DisplayRow label="Internal docs" value="/internal"/>
            <div style={{ borderBottom:`1px solid ${p.hair}` }}/>
            <DisplayRow label="Photographer apply" value="Open" last/>
          </Card>
          <Card p={p} title="Coverage">
            <DisplayRow label="Region" value="Canada" last/>
          </Card>
        </>
      )}

      {kind === 'brokerage_manager' && (
        <>
          <Card p={p} title="Office">
            <div style={{ padding:'16px var(--bx-pad)', borderBottom:`1px solid ${p.hair}` }}>
              <div style={{ fontSize:12, color:p.ink3, marginBottom:8 }}>Name</div>
              <input value={officeDraft.brokerage_name} onChange={e => setOfficeDraft(d => ({ ...d, brokerage_name: e.target.value }))}
                style={{ width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:'transparent', outline:'none', boxSizing:'border-box' }}/>
            </div>
            <div style={{ padding:'16px var(--bx-pad)', borderBottom:`1px solid ${p.hair}` }}>
              <div style={{ fontSize:12, color:p.ink3, marginBottom:8 }}>Notification email</div>
              <input type="email" value={officeDraft.notification_email} onChange={e => setOfficeDraft(d => ({ ...d, notification_email: e.target.value }))}
                style={{ width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:'transparent', outline:'none', boxSizing:'border-box' }}/>
            </div>
            <div style={{ padding:'16px var(--bx-pad)' }}>
              <div style={{ fontSize:12, color:p.ink3, marginBottom:8 }}>Monthly budget (CAD)</div>
              <input type="number" value={officeDraft.monthly_budget} onChange={e => setOfficeDraft(d => ({ ...d, monthly_budget: e.target.value }))}
                style={{ width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:'transparent', outline:'none', boxSizing:'border-box' }}/>
              <MainCtaScope><Btn data-testid="settings-save" onClick={saveOffice} disabled={saving} style={{ marginTop:14 }}>
                {saving ? 'Saving…' : 'Save office settings'}
              </Btn></MainCtaScope>
            </div>
          </Card>
          <Card p={p} title="Defaults">
            <DisplayRow label="Default package" value="Studio"/>
            <div style={{ borderBottom:`1px solid ${p.hair}` }}/>
            <DisplayRow label="Turnaround SLA" value="48 hours"/>
            <div style={{ borderBottom:`1px solid ${p.hair}` }}/>
            <DisplayRow label="Auto-assign vendor" value="Off" last/>
          </Card>
          <NotifCard p={p} kind={kind} email={profileEmail || notifEmail}/>
        </>
      )}

      {kind === 'team_lead' && (
        <>
          <Card p={p} title="Team">
            <div style={{ padding:'16px var(--bx-pad)' }}>
              <div style={{ fontSize:12, color:p.ink3, marginBottom:8 }}>Name</div>
              <input value={teamDraft} onChange={e => setTeamDraft(e.target.value)}
                style={{ width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:'transparent', outline:'none', boxSizing:'border-box' }}/>
              <MainCtaScope><Btn data-testid="settings-save" onClick={saveTeam} disabled={saving} style={{ marginTop:14 }}>
                {saving ? 'Saving…' : 'Save team name'}
              </Btn></MainCtaScope>
            </div>
          </Card>
          <NotifCard p={p} kind={kind} email={profileEmail || notifEmail}/>
        </>
      )}

      {(kind === 'agent' || kind === 'team_member' || kind === 'solo' || isBuilder) && (
        <>
          <Card p={p} title="Profile">
            <DisplayRow label="Name" value={profileName}/>
            <div style={{ borderBottom:`1px solid ${p.hair}` }}/>
            <DisplayRow label="Email" value={profileEmail} last/>
          </Card>
          <NotifCard p={p} kind={kind} email={profileEmail || notifEmail}/>
        </>
      )}

      {!isBuilder && <Card p={p} title="Connected tools">
        <div style={{ padding:'16px var(--bx-pad)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 }}>
          <div>
            <div style={{ fontSize:14 }}>MLS, calendar, e-sign and contacts</div>
            <div style={{ fontSize:12, color:p.ink3, marginTop:4 }}>Connect your MLS feed and calendar.</div>
          </div>
          <Link to="/integrations" style={{ display:'inline-flex', alignItems:'center', minHeight:44, minWidth:44, fontSize:13, color:p.ink2, textDecoration:'underline', textUnderlineOffset:3, fontFamily:F.display, whiteSpace:'nowrap' }}>Open</Link>
        </div>
      </Card>}

      <PasskeysCard p={p} primary={kind !== 'brokerage_manager' && kind !== 'team_lead'} />

      <AppearanceCard p={p} theme={theme} setTheme={setTheme} themeOptions={themeOptions}/>

      <DeleteAccountCard p={p} />

    </div>
  );
}
